import React, { useEffect, useMemo, useState } from 'react';
import tradingService from '../services/tradingService';

const isCanceledRequest = (err) => {
  if (!err) return false;
  return (
    err?.name === 'AbortError' || // fetch / some libs
    err?.name === 'CanceledError' || // axios v1
    err?.name === 'CanceledError' || // axios v1
    err?.code === 'ERR_CANCELED' || // axios v1
    String(err?.message || '').toLowerCase() === 'canceled'
  );
};

export default function AccountBalancesSection({ number }) {
  // balances state (paginated)
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balancesRefreshing, setBalancesRefreshing] = useState(false);
  const [balancesHasLoaded, setBalancesHasLoaded] = useState(false);

  const [balancesError, setBalancesError] = useState('');
  const [balances, setBalances] = useState([]);
  const [balancesCount, setBalancesCount] = useState(0);
  const [balancesLimit, setBalancesLimit] = useState(10);
  const [balancesOffset, setBalancesOffset] = useState(0);
  const [reloadNonce, setReloadNonce] = useState(0);

  const balancesPage = useMemo(
    () => Math.floor(balancesOffset / Math.max(1, balancesLimit)) + 1,
    [balancesOffset, balancesLimit]
  );
  const balancesTotalPages = useMemo(
    () => Math.max(1, Math.ceil((balancesCount || 0) / Math.max(1, balancesLimit))),
    [balancesCount, balancesLimit]
  );
  const balancesCanPrev = balancesOffset > 0;
  const balancesCanNext = balancesOffset + balancesLimit < (balancesCount || 0);

  useEffect(() => {
    if (!number) return;

    const id = window.setInterval(() => {
      setReloadNonce((n) => n + 1);
    }, 10_000);

    return () => window.clearInterval(id);
  }, [number, balancesLimit, balancesOffset]);

  useEffect(() => {
    if (!number) return;

    const ac = new AbortController();
    let alive = true;

    (async () => {
      const isBackground = balancesHasLoaded;

      try {
        if (isBackground) setBalancesRefreshing(true);
        else setBalancesLoading(true);

        setBalancesError('');

        const res = await tradingService.accounts.balances.listForBalances({
          number,
          limit: balancesLimit,
          offset: balancesOffset,
          signal: ac.signal,
        });

        if (!alive) return;

        const payload = res?.data ?? {};
        const list = payload?.balances ?? [];
        setBalances(Array.isArray(list) ? list : []);
        setBalancesCount(Number(payload?.count ?? 0));
        setBalancesHasLoaded(true);
      } catch (err) {
        if (!alive || isCanceledRequest(err) || ac.signal.aborted) return;

        // keep last good data to avoid blinking
        setBalancesError(err?.message || 'Failed to load balances');
      } finally {
        if (!alive) return;
        setBalancesLoading(false);
        setBalancesRefreshing(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [number, balancesLimit, balancesOffset, reloadNonce, balancesHasLoaded]);


  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '—';

    // Normalize scientific notation strings like "0e-8" / "1.23E-7"
    if (typeof value === 'string') {
      const s = value.trim();
      const sci = /^-?\d+(\.\d+)?e[+-]?\d+$/i;
      if (sci.test(s)) {
        const n = Number(s);
        if (!Number.isFinite(n)) return s;

        const fixed = n.toFixed(8);
        return fixed.replace(/\.?0+$/, '');
      }

      return s;
    }

    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return String(value);
      const fixed = value.toFixed(8);
      return fixed.replace(/\.?0+$/, '');
    }

    return String(value);
  };

  return (
    <section className="account-details__section">
      <div className="account-details__section-header">
        <div>
          <div className="account-details__section-title">Balances</div>
          <div className="account-details__section-subtitle">
            {balancesCount ? (
              <>
                Showing {balancesOffset + 1}–{Math.min(balancesOffset + balancesLimit, balancesCount)} of {balancesCount}
              </>
            ) : (
              'Balances for this account.'
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, opacity: 0.8 }}>Page size</span>
            <select
              value={String(balancesLimit)}
              onChange={(e) => {
                const next = Math.max(1, Number(e.target.value) || 10);
                setBalancesOffset(0);
                setBalancesLimit(next);
              }}
              className="account-details__control"
              style={{ width: 110 }}
              disabled={balancesLoading || balancesRefreshing}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </label>

          <button
            type="button"
            className="account-details__btn"
            onClick={() => setBalancesOffset((o) => Math.max(0, o - balancesLimit))}
            disabled={!balancesCanPrev || balancesLoading || balancesRefreshing}
          >
            Prev
          </button>
          <button
            type="button"
            className="account-details__btn"
            onClick={() => setBalancesOffset((o) => o + balancesLimit)}
            disabled={!balancesCanNext || balancesLoading || balancesRefreshing}
          >
            Next
          </button>

          <div style={{ fontSize: 12, opacity: 0.8 }}>
            Page {balancesPage} / {balancesTotalPages}
          </div>

          <button
            type="button"
            className="account-details__btn"
            onClick={() => setReloadNonce((n) => n + 1)}
            disabled={balancesLoading || balancesRefreshing}
            title="Reload current page"
          >
            Refresh
          </button>

          {balancesRefreshing ? (
            <div style={{ fontSize: 12, opacity: 0.75, whiteSpace: 'nowrap' }}>Refreshing…</div>
          ) : null}
        </div>
      </div>

      {balancesError ? <div className="account-details__error">{balancesError}</div> : null}
      {balancesLoading && !balancesHasLoaded ? <div>Loading…</div> : null}

      {!balancesLoading && !balancesError ? (
        balances.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Date</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Asset</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Free</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Locked</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Borrowed</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Interest</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Net</th>
                </tr>
              </thead>
              <tbody>
                {balances.map((b) => (
                  <tr key={`${b?.date ?? '—'}:${b?.asset ?? '—'}`}>
                    <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{b?.date ?? '—'}</td>
                    <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                      <code>{b?.asset ?? '—'}</code>
                    </td>
                    <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatNumber(b?.free)}</td>
                    <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatNumber(b?.locked)}</td>
                    <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatNumber(b?.borrowed)}</td>
                    <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatNumber(b?.interest)}</td>
                    <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatNumber(b?.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div>No balances found.</div>
        )
      ) : null}
    </section>
  );
}